import { useEffect, useId, useRef, useState } from 'react';

// Couleur du bouton d'action selon la gravité. 'warning' = orange (défaut
// historique, inchangé) ; 'danger' = rouge (action destructive/irréversible).
const VARIANT_BTN = {
  warning: 'bg-orange-500 hover:bg-orange-600 shadow-orange-500/20',
  danger: 'bg-red-600 hover:bg-red-700 shadow-red-600/20',
};

// Boîte de dialogue (rôle `dialog`, modale) : le focus part sur le bouton qui
// ne change rien, le choix par défaut, reste dans la boîte (Tab boucle sur
// ses deux boutons) et revient à l'élément d'origine à la fermeture ; Échap
// équivaut au choix par défaut.
const ConfirmModal = ({ title, message, confirmLabel = 'Supprimer', cancelLabel = 'Annuler', variant = 'warning', onConfirm, onCancel }) => {
  const titreId = useId();
  const boite = useRef(null);
  const annuler = useRef(onCancel);
  annuler.current = onCancel;
  // Lu au premier rendu : `autoFocus` déplace le focus avant les effets.
  const [origine] = useState(() => document.activeElement);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { annuler.current(); return; }
      if (e.key !== 'Tab' || !boite.current) return;
      const boutons = [...boite.current.querySelectorAll('button')];
      const premier = boutons[0];
      const dernier = boutons[boutons.length - 1];
      if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
      else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
      else if (!boite.current.contains(document.activeElement)) { e.preventDefault(); premier.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (origine && typeof origine.focus === 'function' && document.contains(origine)) origine.focus();
    };
  }, [origine]);
  return (
    // z-[10050] : une CONFIRMATION doit passer au-dessus de tous les overlays
    // de l'app (popover « réutiliser » z-[10003], modal édition z-[10001],
    // portails élève z-[10030]) — à 1100, elle s'affichait SOUS le popover.
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div ref={boite} role="dialog" aria-modal="true" aria-labelledby={titreId} className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6">
        <h3 id={titreId} className={`text-lg font-bold text-gray-800 ${message ? 'mb-2' : 'mb-5'}`}>{title}</h3>
        {message && <p className="text-sm text-gray-500 mb-5">{message}</p>}
        <div className="flex justify-end gap-2">
          <button
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-400 focus:outline-none transition"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md focus-visible:ring-2 focus-visible:ring-blue-400 focus:outline-none transition ${VARIANT_BTN[variant] || VARIANT_BTN.warning}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
